import React, { useState, useMemo } from 'react';

const ReportNMI = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVamp, setShowVamp] = useState(false);
  const [vampSort, setVampSort] = useState(null); // null, 'asc', 'desc'
  
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const [startDate, setStartDate] = useState(thirtyDaysAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  const formatDateForNMI = (dateString) => {
    return dateString.replace(/-/g, '');
  };

  const fetchReport = async (e) => {
    if (e) e.preventDefault();
    
    setLoading(true);
    setError(null);
    setVampSort(null);
    
    try {
      const nmiStartDate = formatDateForNMI(startDate);
      const nmiEndDate = formatDateForNMI(endDate);
      
      let url = `/api/reports?startDate=${nmiStartDate}&endDate=${nmiEndDate}`;
      if (showVamp) {
        url += '&showVamp=true';
      }
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to fetch report');
      }

      const data = await response.json();
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Sort gateways by VAMP rate
  const sortedGateways = useMemo(() => {
    if (!report || !report.gateways) return [];
    if (!vampSort) return report.gateways;
    
    return [...report.gateways].sort((a, b) => {
      const aRate = a.vamp?.vampRate ?? -1;
      const bRate = b.vamp?.vampRate ?? -1;
      return vampSort === 'asc' ? aRate - bRate : bRate - aRate;
    });
  }, [report, vampSort]);

  const toggleVampSort = () => {
    if (!vampSort) setVampSort('desc');
    else if (vampSort === 'desc') setVampSort('asc');
    else setVampSort(null);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getVampColor = (status) => {
    switch (status) {
      case 'STANDARD': return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: '🟢' };
      case 'WARNING': return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: '🟡' };
      case 'VAMP': return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: '🟠' };
      case 'VDMP': return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: '🔴' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', icon: '⚪' };
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">NMI Reports</h2>
        <p className="text-gray-600 mt-2">Transaction analytics across all gateways</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <form onSubmit={fetchReport} className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate}
              required
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              max={today.toISOString().split('T')[0]}
              required
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-smooth"
            />
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:ring-4 focus:ring-primary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
            >
              {loading ? '⏳ Loading...' : '📊 Generate Report'}
            </button>
          </div>
        </form>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button onClick={() => {
              const last7Days = new Date(today);
              last7Days.setDate(today.getDate() - 7);
              setStartDate(last7Days.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }} disabled={loading} className="text-sm px-3 py-1 text-primary-600 border border-primary-300 rounded hover:bg-primary-50 disabled:opacity-50 transition-smooth">
            Last 7 Days
          </button>
          <button onClick={() => {
              const last30Days = new Date(today);
              last30Days.setDate(today.getDate() - 30);
              setStartDate(last30Days.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }} disabled={loading} className="text-sm px-3 py-1 text-primary-600 border border-primary-300 rounded hover:bg-primary-50 disabled:opacity-50 transition-smooth">
            Last 30 Days
          </button>
          <button onClick={() => {
              const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
              setStartDate(firstDayOfMonth.toISOString().split('T')[0]);
              setEndDate(today.toISOString().split('T')[0]);
            }} disabled={loading} className="text-sm px-3 py-1 text-primary-600 border border-primary-300 rounded hover:bg-primary-50 disabled:opacity-50 transition-smooth">
            This Month
          </button>

          {/* VAMP Rate Checkbox */}
          <label className={`flex items-center gap-2 text-sm px-3 py-1 rounded border cursor-pointer select-none transition-smooth ${
            showVamp 
              ? 'bg-red-50 border-red-300 text-red-700' 
              : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}>
            <input
              type="checkbox"
              checked={showVamp}
              onChange={(e) => setShowVamp(e.target.checked)}
              disabled={loading}
              className="rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
            <span>🧛 Show VAMP Rate</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">❌ {error}</p>
        </div>
      )}

      {loading && (
        <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
          <p className="text-blue-800 font-medium">
            {showVamp ? '⏳ Generating report + VAMP rates (NMI + CB360)...' : '⏳ Generating report for all gateways...'}
          </p>
          <p className="text-sm text-blue-600 mt-1">
            {showVamp ? 'This may take up to 90 seconds (querying NMI + Chargeback360)' : 'This may take up to 60 seconds'}
          </p>
        </div>
      )}

      {report && (
        <>
          {/* Sort by VAMP button */}
          {report.gateways.some(gw => gw.vamp) && (
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={toggleVampSort}
                className={`text-sm px-4 py-2 rounded-lg border font-medium transition-smooth ${
                  vampSort
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                🧛 Sort by VAMP Rate {vampSort === 'desc' ? '↓' : vampSort === 'asc' ? '↑' : ''}
              </button>
              {vampSort && (
                <span className="text-xs text-gray-500">
                  {vampSort === 'desc' ? 'Highest first' : 'Lowest first'} • Click again to toggle
                </span>
              )}
            </div>
          )}

          <div className="space-y-4 mb-8">
            {sortedGateways.map((gateway) => (
              <div key={gateway.gatewayId} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {gateway.error ? (
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{gateway.gatewayName}</h3>
                    <p className="text-sm text-red-600">Error: {gateway.error}</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{gateway.gatewayName}</h3>
                        {gateway.cb360GatewayId && (
                          <span className="text-xs text-gray-400 font-mono">CB360 GW: {gateway.cb360GatewayId}</span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-primary-600">{formatAmount(gateway.totalAmount)}</p>
                        <p className="text-sm text-gray-500">{gateway.totalCount} transactions</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">USA Transactions</p>
                        <p className="text-2xl font-bold text-blue-600">{gateway.usaCount}</p>
                        <p className="text-sm text-blue-700 font-medium">{formatPercentage(gateway.usaRate)}</p>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">International</p>
                        <p className="text-2xl font-bold text-green-600">{gateway.internationalCount}</p>
                        <p className="text-sm text-green-700 font-medium">{formatPercentage(gateway.internationalRate)}</p>
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Total Count</p>
                        <p className="text-2xl font-bold text-gray-900">{gateway.totalCount}</p>
                        <p className="text-sm text-gray-500">All transactions</p>
                      </div>

                      <div className="bg-purple-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                        <p className="text-2xl font-bold text-purple-600">{formatAmount(gateway.totalAmount)}</p>
                        <p className="text-sm text-purple-700">Revenue</p>
                      </div>
                    </div>

                    {/* VAMP Rate Section */}
                    {gateway.vamp && (
                      <div className="mt-4">
                        {gateway.vamp.error && !gateway.vamp.vampRate && gateway.vamp.vampRate !== 0 ? (
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-500">🧛 VAMP: {gateway.vamp.error}</p>
                          </div>
                        ) : (
                          <div className={`p-4 rounded-lg border ${getVampColor(gateway.vamp.vampStatus).bg} ${getVampColor(gateway.vamp.vampStatus).border}`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">🧛</span>
                                <span className="font-bold text-gray-900">VAMP Rate</span>
                                <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${getVampColor(gateway.vamp.vampStatus).bg} ${getVampColor(gateway.vamp.vampStatus).text}`}>
                                  {getVampColor(gateway.vamp.vampStatus).icon} {gateway.vamp.vampStatus}
                                </span>
                              </div>
                              <div className="text-right">
                                <p className={`text-3xl font-bold ${getVampColor(gateway.vamp.vampStatus).text}`}>
                                  {gateway.vamp.vampRate !== null ? `${gateway.vamp.vampRate.toFixed(4)}%` : 'N/A'}
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div className="bg-white bg-opacity-60 p-2 rounded">
                                <p className="text-gray-500">Visa Approved</p>
                                <p className="font-bold text-gray-900">{(gateway.vamp.visaApproved || 0).toLocaleString()}</p>
                              </div>
                              <div className="bg-white bg-opacity-60 p-2 rounded">
                                <p className="text-gray-500">Visa CBs</p>
                                <p className="font-bold text-gray-900">{gateway.vamp.visaCbs || 0}</p>
                              </div>
                              <div className="bg-white bg-opacity-60 p-2 rounded">
                                <p className="text-gray-500">RDR</p>
                                <p className="font-bold text-gray-900">{gateway.vamp.rdrCount || 0}</p>
                              </div>
                              <div className="bg-white bg-opacity-60 p-2 rounded">
                                <p className="text-gray-500">Visa Disputes</p>
                                <p className="font-bold text-gray-900">{gateway.vamp.totalDisputes || 0}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold mb-6">Grand Totals Across All Gateways</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div>
                <p className="text-primary-100 text-sm mb-2">Total Revenue</p>
                <p className="text-4xl font-bold">{formatAmount(report.grandTotals.totalAmount)}</p>
              </div>

              <div>
                <p className="text-primary-100 text-sm mb-2">Total Transactions</p>
                <p className="text-4xl font-bold">{report.grandTotals.totalCount.toLocaleString()}</p>
              </div>

              <div>
                <p className="text-primary-100 text-sm mb-2">Geographic Split</p>
                <div className="flex gap-4">
                  <div>
                    <p className="text-2xl font-bold">{report.grandTotals.usaCount}</p>
                    <p className="text-sm text-primary-100">USA ({formatPercentage(report.grandTotals.usaRate)})</p>
                  </div>
                  <div className="border-l border-primary-400 pl-4">
                    <p className="text-2xl font-bold">{report.grandTotals.internationalCount}</p>
                    <p className="text-sm text-primary-100">Intl ({formatPercentage(report.grandTotals.internationalRate)})</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Grand Total VAMP */}
            {report.grandTotals.vamp && (
              <div className="mt-6 pt-6 border-t border-primary-400">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-primary-100 text-sm mb-1">🧛 Overall VAMP Rate (All Gateways)</p>
                    <p className="text-4xl font-bold">
                      {report.grandTotals.vamp.vampRate !== null 
                        ? `${report.grandTotals.vamp.vampRate.toFixed(4)}%` 
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right text-sm text-primary-100 space-y-1">
                    <p>Visa Approved: {report.grandTotals.vamp.totalVisaApproved.toLocaleString()}</p>
                    <p>Visa CBs: {report.grandTotals.vamp.totalVisaCbs} | RDR: {report.grandTotals.vamp.totalRdr}</p>
                    <p>Visa Disputes: {report.grandTotals.vamp.totalDisputes}</p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-primary-100 text-sm mt-6">
              Last updated: {new Date(report.timestamp).toLocaleString()}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportNMI;
